import React from "react";
import { useTranslation } from "react-i18next";
import Input from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  TextType,
  DropdownList,
  RadioButton,
  CheckboxSquare,
  StarRating,
  ScaleRating,
  Delete,
  Plus,
  Close,
} from "@/components/icons";
import type { QuestionType } from "../QuestionTypeSelector";
import styles from "./QuestionCard.module.scss";

export interface QuestionOption {
  id: string;
  value: string;
}

export interface PollQuestionData {
  id: string;
  type: QuestionType;
  questionText: string;
  options: QuestionOption[];
  required: boolean;
}

interface QuestionCardProps {
  question: PollQuestionData;
  onUpdate: (question: PollQuestionData) => void;
  onDelete: (questionId: string) => void;
  className?: string;
}

const getQuestionTypeIcon = (type: QuestionType) => {
  switch (type) {
    case "text":
      return TextType;
    case "dropdown":
      return DropdownList;
    case "single_choice":
      return RadioButton;
    case "multiple_choice":
      return CheckboxSquare;
    case "star_rating":
      return StarRating;
    case "scale":
      return ScaleRating;
    default:
      return TextType;
  }
};

const getQuestionTypeLabel = (
  type: QuestionType,
  t: (key: string) => string,
) => {
  switch (type) {
    case "text":
      return t("createPoll.questionTypes.text");
    case "dropdown":
      return t("createPoll.questionTypes.dropdown");
    case "single_choice":
      return t("createPoll.questionTypes.singleChoice");
    case "multiple_choice":
      return t("createPoll.questionTypes.multipleChoice");
    case "star_rating":
      return t("createPoll.questionTypes.starRating");
    case "scale":
      return t("createPoll.questionTypes.scale");
    default:
      return "";
  }
};

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  onUpdate,
  onDelete,
  className = "",
}) => {
  const { t } = useTranslation();
  const Icon = getQuestionTypeIcon(question.type);
  const typeLabel = getQuestionTypeLabel(question.type, t);

  const handleQuestionTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ ...question, questionText: e.target.value });
  };

  const handleRequiredChange = () => {
    onUpdate({ ...question, required: !question.required });
  };

  const handleOptionChange = (
    optionId: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const updatedOptions = question.options.map((opt) =>
      opt.id === optionId ? { ...opt, value: e.target.value } : opt,
    );
    onUpdate({ ...question, options: updatedOptions });
  };

  const handleAddOption = () => {
    const newOption: QuestionOption = {
      id: `option-${Date.now()}`,
      value: "",
    };
    onUpdate({ ...question, options: [...question.options, newOption] });
  };

  const handleRemoveOption = (optionId: string) => {
    const updatedOptions = question.options.filter(
      (opt) => opt.id !== optionId,
    );
    onUpdate({ ...question, options: updatedOptions });
  };

  const hasOptions = ["dropdown", "single_choice", "multiple_choice"].includes(
    question.type,
  );

  return (
    <div className={`${styles.card} ${className}`}>
      <div className={styles.cardHeader}>
        <div className={styles.typeInfo}>
          <Icon width={20} height={20} className={styles.typeIcon} />
          <span className={styles.typeLabel}>{typeLabel}</span>
        </div>
        <div className={styles.headerActions}>
          <Checkbox
            label={t("createPoll.questions.required")}
            value={question.required}
            onChange={handleRequiredChange}
          />
          <button
            type="button"
            className={styles.deleteButton}
            onClick={() => onDelete(question.id)}
            aria-label={t("common.delete")}
          >
            <Delete width={20} height={20} />
          </button>
        </div>
      </div>

      <div className={styles.cardBody}>
        <Input
          placeholder={t("createPoll.questions.questionPlaceholder")}
          value={question.questionText}
          onChange={handleQuestionTextChange}
        />

        {hasOptions && (
          <div className={styles.optionsSection}>
            {question.options.map((option) => (
              <div key={option.id} className={styles.optionRow}>
                <div className={styles.optionInputWrapper}>
                  <Input
                    placeholder={t("createPoll.questions.optionPlaceholder")}
                    value={option.value}
                    onChange={(e) => handleOptionChange(option.id, e)}
                  />
                </div>
                <button
                  type="button"
                  className={styles.removeOptionButton}
                  onClick={() => handleRemoveOption(option.id)}
                  aria-label={t("common.remove")}
                >
                  <Close width={16} height={16} color="#707A8F" />
                </button>
              </div>
            ))}
            <button
              type="button"
              className={styles.addOptionButton}
              onClick={handleAddOption}
            >
              <Plus width={16} height={16} color="#e7212b" />
              <span style={{ textDecoration: "underline" }}>
                {t("createPoll.questions.addOption")}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionCard;
